import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  UserCheck,
  Plus,
  AlertTriangle,
  X,
  RefreshCw,
  Search,
  Trash2,
  Pencil,
} from 'lucide-react';
import MainService from '../../services/MainService';
import { normalizeVehicleGridResponse } from '../../utils/vehicleApiUtils';

const defaultPageSize = 20;

const VehicleAssignments = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState(vehicleId || '');

  const effectiveVehicleId = vehicleId || vehicleFilter || null;

  const loadVehicles = useCallback(async () => {
    if (vehicleId) return;
    try {
      const list = await MainService.VehiclesInDropDown();
      setVehicles(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setVehicles([]);
    }
  }, [vehicleId]);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const body = {
        Page: page,
        PageSize: pageSize,
        Keyword: keyword || '',
      };
      if (effectiveVehicleId) {
        body.VehicleId = Number(effectiveVehicleId);
      }
      const res = await MainService.VehicleAssignmentsInGrid(body);
      const { data, totalCount } = normalizeVehicleGridResponse(res);
      setAssignments(Array.isArray(data) ? data : []);
      setTotalCount(totalCount);
    } catch (err) {
      console.error('Error loading assignments:', err);
      setError(err.message || 'Failed to load assignments');
      setAssignments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword, effectiveVehicleId]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  useEffect(() => {
    if (vehicleId) {
      setVehicleFilter(vehicleId);
    }
  }, [vehicleId]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleDelete = async (row) => {
    const id = row.Id ?? row.ID;
    if (!id || !window.confirm('Remove this assignment?')) return;
    try {
      await MainService.DeleteVehicleAssignment(id);
      await loadAssignments();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Delete failed');
    }
  };

  const goEdit = (row) => {
    const vid = row.VehicleId ?? row.VehicleID ?? effectiveVehicleId;
    const aid = row.Id ?? row.ID;
    if (vid && aid) {
      navigate(`/home/vehicle-management/add-assignment/${vid}/${aid}`);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div className="flex items-center">
            <UserCheck className="w-6 h-6 text-primary mr-2" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Vehicle assignments</h1>
              <p className="text-sm text-gray-500 mt-1">Drivers and employee drivers linked to vehicles</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                navigate(
                  effectiveVehicleId
                    ? `/home/vehicle-management/add-assignment/${effectiveVehicleId}`
                    : '/home/vehicle-management/add-assignment'
                )
              }
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Assign driver</span>
            </button>
            <button
              type="button"
              onClick={loadAssignments}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-[#46955A] text-[#46955A] rounded-lg hover:bg-[#f0f9f4] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-4">
          {!vehicleId && (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Vehicle</label>
              <select
                value={vehicleFilter}
                onChange={(e) => {
                  setVehicleFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">All vehicles</option>
                {vehicles.map((v) => (
                  <option key={v.Id ?? v.id} value={v.Id ?? v.id}>
                    {v.PlateNumber || v.Number || v.Name || v.Text || v.Id}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(1);
                }}
                placeholder="Keyword..."
                className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        {loading && assignments.length === 0 ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-[#46955A] animate-spin mx-auto" />
          </div>
        ) : assignments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No assignments found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unassigned</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignments.map((assignment) => (
                  <tr key={assignment.Id ?? assignment.ID} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {assignment.VehiclePlate || assignment.VehicleNumber || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {assignment.DriverName || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {assignment.EmployeeName || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {assignment.AssignedDate || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {assignment.UnassignedDate || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          assignment.IsActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {assignment.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="inline-flex items-center justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => goEdit(assignment)}
                          title="Edit"
                          aria-label="Edit"
                          className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(assignment)}
                          title="Remove"
                          aria-label="Remove"
                          className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
          <span>
            Page {page} of {totalPages} · {totalCount} records
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 rounded-lg border border-[#46955A] text-[#46955A] hover:bg-[#f0f9f4] disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 rounded-lg border border-[#46955A] text-[#46955A] hover:bg-[#f0f9f4] disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleAssignments;
